import { useMemo, useState } from "react";
import { Form } from "react-final-form";
import { useTranslation } from "react-i18next";
import { useValidationSchema } from "src/ui/utils/useValidationSchema";
import { getSafeExternalUrl } from "src/ui/utils/validityChecker";

import { useCurrentWorkspaceProducts } from "@products/api/helpers";
import { PrimaryButton, PrimaryOutlinedButton, SecondaryButton } from "@shared/CustomButton";
import { SectionPanel } from "@shared/SectionPanel/SectionPanel";
import { TextLink } from "@shared/TextLink";
import { showErrorToast, showSuccessToast } from "@shared/toasts/Toasts";
import { getErrorMessage } from "@shared/toasts/useQueryErrorToast";

import { useUpdateWorkspaceMutation } from "../../api";
import { displayOverviewValue, EditableField, FormColumn } from "../WorkspaceFormColumn/FormColumn";
import {
    OrganizationDetailsFormValues,
    organizationDetailsValidationSchema,
} from "../WorkspaceFormColumn/WorkspaceFormColumn.validation";

type OrganizationDetailsSectionProps = {
    workspace: API.Workspace;
    canEdit: boolean;
};

function trimmedValue(value: string | undefined) {
    return value?.trim() ?? "";
}

function organizationWebsiteValue(url: string | undefined) {
    const trimmed = trimmedValue(url);
    const safeUrl = getSafeExternalUrl(url);
    if (!safeUrl) {
        return displayOverviewValue(trimmed);
    }

    return (
        <TextLink href={safeUrl} target="_blank" tooltip={safeUrl}>
            {trimmed}
        </TextLink>
    );
}

export function OrganizationDetailsSection(props: OrganizationDetailsSectionProps) {
    const { t } = useTranslation(["common", "pages"]);
    const [updateWorkspace] = useUpdateWorkspaceMutation();
    const [editing, setEditing] = useState(false);
    const validate = useValidationSchema(organizationDetailsValidationSchema);
    const { data: products = [] } = useCurrentWorkspaceProducts();
    const hasPublicProducts = products.some((product) => product.isPublic);

    const { organizationName, organizationVatId, organizationUrl } = props.workspace;
    const initialValues = useMemo<OrganizationDetailsFormValues>(
        () => ({
            organizationName: organizationName ?? "",
            organizationVatId: organizationVatId ?? "",
            organizationUrl: organizationUrl ?? "",
        }),
        [organizationName, organizationVatId, organizationUrl],
    );

    const organizationFields: EditableField<keyof OrganizationDetailsFormValues>[] = [
        {
            name: "organizationName",
            label: t("pages:workspacePage.input.organizationName.caption"),
            disabled: hasPublicProducts,
            disabledTooltip: t("pages:workspacePage.tooltip.organizationNameLockedByPublicProducts"),
        },
        { name: "organizationVatId", label: t("pages:workspacePage.input.organizationVatId.caption") },
        {
            name: "organizationUrl",
            label: t("pages:workspacePage.input.organizationUrl.caption"),
            placeholder: t("pages:workspacePage.input.organizationUrl.placeholder"),
            renderValue: organizationWebsiteValue,
            disabled: hasPublicProducts,
            disabledTooltip: t("pages:workspacePage.tooltip.organizationUrlLockedByPublicProducts"),
        },
    ];

    const onSubmit = async (values: OrganizationDetailsFormValues) => {
        try {
            await updateWorkspace({
                ...props.workspace,
                organizationName: hasPublicProducts
                    ? (props.workspace.organizationName ?? "")
                    : trimmedValue(values.organizationName),
                organizationVatId: trimmedValue(values.organizationVatId),
                organizationUrl: hasPublicProducts
                    ? (props.workspace.organizationUrl ?? "")
                    : trimmedValue(values.organizationUrl),
            }).unwrap();
            showSuccessToast(t("pages:workspacePage.toast.workspaceUpdateSuccess"));
            setEditing(false);
        } catch (error) {
            showErrorToast(getErrorMessage(error));
        }
    };

    return (
        <Form<OrganizationDetailsFormValues>
            initialValues={initialValues}
            onSubmit={onSubmit}
            validate={validate}
            render={({ handleSubmit, form, invalid, pristine, submitting }) => (
                <form onSubmit={handleSubmit} noValidate>
                    <SectionPanel
                        title={t("pages:workspacePage.section.title.organizationDetails")}
                        actions={
                            editing ? (
                                <>
                                    <SecondaryButton
                                        type="button"
                                        onClick={() => {
                                            form.reset();
                                            setEditing(false);
                                        }}
                                        disabled={submitting}
                                    >
                                        {t("common:button.cancel")}
                                    </SecondaryButton>
                                    <PrimaryButton
                                        type="submit"
                                        disabled={invalid || pristine || submitting}
                                        loading={submitting}
                                    >
                                        {t("common:button.save")}
                                    </PrimaryButton>
                                </>
                            ) : (
                                <PrimaryOutlinedButton
                                    onClick={() => setEditing(true)}
                                    disabled={!props.canEdit}
                                    startIcon={<i className="fa-solid fa-pencil" />}
                                    tooltipProps={{
                                        title: t("pages:workspacePage.tooltip.workspaceRoleChangeNotAllowed"),
                                        hide: props.canEdit,
                                        placement: "top",
                                    }}
                                >
                                    {t("common:button.edit")}
                                </PrimaryOutlinedButton>
                            )
                        }
                    >
                        <FormColumn fields={organizationFields} values={initialValues} editing={editing} />
                    </SectionPanel>
                </form>
            )}
        />
    );
}
